'use client';

import { Grid, Heading, Legend, TextPassage } from '@petsmart-ui/sparky';
import { NEW_TAB, TARGET_BLANK, TARGET_SELF } from '@libs/contentful';
import type {
    BenefitsItem,
    LinkAction,
    PRIMARY_BUTTON_STYLE,
    SECONDARY_BUTTON_STYLE,
    LoyaltyBenefitsItem,
} from '@libs/contentful';
import { useEffect, useMemo, useState } from 'react';
import { useCart } from '@libs/common-next';
import { DeliveryType, NAVIGATION_URLS } from '@libs/core';
import type { SignInFormProps } from '../SignInForm';
import { SignInForm } from '../SignInForm';
import { BenefitItems } from '../BenefitItems';
import type { CTAWithLinkEntry } from './utils';
import { getButtonVariant } from './utils';
import { GenericButton } from '../GenericButton';

import './InterstitialSignIn.scss';
import type { TreatsLoginLocation } from '@libs/analytics';

export type InterstitialCta = {
    fields: CTAWithLinkEntry;
};

export type InterstitialLinkAction = {
    fields: LinkAction;
};

type InterstitialSignInProps = SignInFormProps & {
    title?: string;
    subtext?: string;
    benefitItems?: BenefitsItem[] | LoyaltyBenefitsItem[];
    createAccountCta?: InterstitialCta;
    itemsTitle?: string;
    createAccountHref?: string;
    displayAutoshipDescription?: boolean;
    location?: TreatsLoginLocation;
    pathOverride?: string;
};

const componentClassName = 'interstitial-sign-in';

export function InterstitialSignIn({
    title,
    subtext,
    benefitItems,
    createAccountCta,
    itemsTitle,
    cta,
    linkAction,
    isInDrawer,
    createAccountHref,
    onSuccessfulSignIn,
    displayAutoshipDescription,
    location,
    pathOverride,
}: Readonly<InterstitialSignInProps>) {
    const [isCheckoutPage, setIsCheckoutPage] = useState(false);

    const { cart } = useCart();
    const currentPage = IS_SSR ? '' : new URL(window.location.href).pathname;

    useEffect(() => {
        setIsCheckoutPage(['/checkout-login', '/cart'].includes(currentPage));
    }, [currentPage]);

    const isAutoship = useMemo(() => {
        if (!cart?.fulfillmentGroups || !displayAutoshipDescription) {
            return false;
        }
        return (
            cart.fulfillmentGroups.length > 0 &&
            cart.fulfillmentGroups.find(
                group =>
                    group.fulfillmentMethod.toLowerCase() ===
                    DeliveryType.AUTOSHIP
            ) !== undefined
        );
    }, [cart?.fulfillmentGroups, displayAutoshipDescription]);

    const createAccountVariant:
        | typeof PRIMARY_BUTTON_STYLE
        | typeof SECONDARY_BUTTON_STYLE = createAccountCta
        ? getButtonVariant(createAccountCta)
        : 'table-header';

    const createAccountLink = isCheckoutPage
        ? `${NAVIGATION_URLS.velocity.createAccount}?redirectUrl=/checkout`
        : createAccountHref ??
          createAccountCta?.fields?.link?.fields?.webUrl ??
          '/create-account';

    return (
        <div data-testid="interstitial-sign-in" className={componentClassName}>
            {isAutoship && (
                <TextPassage
                    capLinelength={false}
                    style={{ marginBottom: '24px' }}
                >
                    All autoship items require an account to checkout. Please
                    sign in or create an account to continue.
                </TextPassage>
            )}

            {(title || subtext) && (
                <Grid.Item className={`${componentClassName}__headings`}>
                    {title && (
                        <Heading
                            tagName="h5"
                            className={`${componentClassName}__heading`}
                        >
                            {title}
                        </Heading>
                    )}
                    {subtext && (
                        <Legend
                            className={`${componentClassName}__sub-heading`}
                            legendSize="sm"
                            required
                            text={subtext}
                        />
                    )}
                </Grid.Item>
            )}
            <SignInForm
                {...{
                    cta,
                    linkAction,
                    isAutoship,
                    isInDrawer,
                    onSuccessfulSignIn,
                    location,
                    pathOverride,
                }}
            />
            <div
                className={
                    isInDrawer
                        ? `${componentClassName}__benefits-container__drawer`
                        : `${componentClassName}__benefits-container`
                }
            >
                {itemsTitle && (
                    <Heading size="body-lg-bold" tagName="h2">
                        {itemsTitle}
                    </Heading>
                )}

                {benefitItems && (
                    <BenefitItems
                        data-testid="benefit-items"
                        items={benefitItems}
                    />
                )}
            </div>
            {benefitItems && (
                <GenericButton
                    variant={createAccountVariant}
                    text={createAccountCta?.fields?.text || 'Create account'}
                    size="lg"
                    target={
                        createAccountCta?.fields?.behavior === NEW_TAB
                            ? TARGET_BLANK
                            : TARGET_SELF
                    }
                    href={createAccountLink}
                    rel="noreferrer"
                    data-testid="create-account-btn"
                    fullWidth
                />
            )}
        </div>
    );
}
